import React, { Dispatch, SetStateAction, useCallback } from 'react'
import { getTicketBrokeragePlatformConfigurationFields } from '@/components/Filter/getBrokeragePlatformConfigurationFields'
import { SortDescriptor } from '@progress/kendo-data-query'
import { PlatformConfigurationTypesDto } from 'api/Trading-Accounts/Types/PlatformConfigurationTypesDto'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { useGridState } from 'features/DataGrid'
import { GridStateStatus } from 'features/DataGrid/hooks/useGridStatusState'
import { CompositeFilterDescriptor } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContextData } from 'utils/TableStateContext'

interface PlatformConfigurationQuery {
  getQuery: (filter?: CompositeFilterDescriptor) => string | null
  getSort: (sortDescriptors?: SortDescriptor[]) => string | null
}

export const usePlatformConfigurationBuildQuery = (): PlatformConfigurationQuery => {
  const { gridState } = useGridState()

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter
      return buildQuery(gridFilter) ?? null
    },
    [gridState.filter] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  return { getQuery, getSort }
}

interface PlatformConfigurationPageProps {
  gridStatusState: GridStateStatus

  onChange(platformConfigurations: PlatformConfigurationTypesDto[]): void

  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
}

export const PlatformConfigurationTableFilter: React.FC<PlatformConfigurationPageProps> = (
  props
) => {
  const { tableData, setTableData } = props
  const { gridStatusState, gridStatusActions } = props.gridStatusState
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const { gridState, gridActions } = useGridState()

  const { getSort, getQuery } = usePlatformConfigurationBuildQuery()

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getTradingAccountPlatformTypesConfiguration({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        props.onChange(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [getQuery, getSort, gridActions, gridState.pageSize, gridState.sort, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <>
      <div>
        <SelectFiltersFacade
          gridState={gridState}
          gridStatusActions={gridStatusActions}
          gridStatusState={gridStatusState}
          setGridFilters={gridActions.setGridFilters}
          filterFetch={filterFetch}
          filterType={FilterType.PlatformConfigurations}
          filterFields={getTicketBrokeragePlatformConfigurationFields()}
          containerClassName='records-clients__filter'
          getQuery={getQuery}
        />
      </div>
    </>
  )
}
